import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, 
  CheckCircle2, 
  Shield, 
  Zap, 
  BarChart3, 
  Smartphone, 
  Lock, 
  Globe,
  Building2,
  Users,
  TrendingUp
} from 'lucide-react';

const FLOWLEDGER_ICON = "https://customer-assets.emergentagent.com/job_flowsecure-fintech/artifacts/jw6e8f7s_7B7D7191-078E-4AB2-A5E6-D847E0D60608.png";
const SIMVAULT_ICON = "https://customer-assets.emergentagent.com/job_flowsecure-fintech/artifacts/xz6y59de_77FA3254-428F-4DC1-8494-71315946C436.png";

export const Home = () => {
  const problems = [
    {
      icon: <BarChart3 className="w-6 h-6 text-[#0080FF]" strokeWidth={1.5} />,
      title: "No Transaction Visibility",
      description: "Businesses struggle to track and confirm mobile money transactions in real-time."
    },
    {
      icon: <Smartphone className="w-6 h-6 text-[#0080FF]" strokeWidth={1.5} />,
      title: "SIM Security Risks",
      description: "Physical SIM cards are vulnerable to theft, loss, and unauthorized access."
    },
    {
      icon: <Globe className="w-6 h-6 text-[#0080FF]" strokeWidth={1.5} />,
      title: "Diaspora Challenges",
      description: "Entrepreneurs abroad cannot securely manage their Liberian business accounts."
    }
  ];

  const solutions = [
    {
      title: "FlowLedger",
      subtitle: "Transaction Tracking Platform",
      description: "Real-time transaction monitoring and confirmation for businesses using mobile money. Never miss a payment again.",
      features: ["Instant SMS Parsing", "Transaction History", "Confirmation Alerts", "Business Analytics"],
      link: "/flowledger",
      icon: FLOWLEDGER_ICON,
      color: "blue"
    },
    {
      title: "SIMVault",
      subtitle: "Secure SIM Hosting",
      description: "Enterprise-grade SIM hosting infrastructure. Securely store and monitor your mobile money SIMs from anywhere in the world.",
      features: ["24/7 Monitoring", "Secure Facility", "Remote Access", "Message Forwarding"],
      link: "/simvault",
      icon: SIMVAULT_ICON,
      color: "slate"
    }
  ];

  const stats = [
    { value: "99.9%", label: "Uptime Guarantee" },
    { value: "50K+", label: "Transactions Tracked" },
    { value: "500+", label: "Businesses Served" },
    { value: "<1s", label: "Alert Delivery" }
  ];

  const trustIndicators = [
    "Central Bank Compliant",
    "Enterprise Security",
    "24/7 Support",
    "Data Encryption"
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-gradient pt-32 pb-20 lg:pt-40 lg:pb-32 relative overflow-hidden">
        <div className="absolute inset-0 pattern-dots opacity-50" />
        
        <div className="container-main relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-blue-50 text-[#0080FF] px-4 py-2 rounded-full text-sm font-medium mb-8 animate-fade-in">
              <Zap className="w-4 h-4" />
              Trusted by businesses across Liberia
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#003C83] tracking-tight leading-tight mb-6 animate-fade-in-up">
              Secure Transaction Infrastructure for{' '}
              <span className="text-[#0080FF]">African Businesses</span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto mb-10 leading-relaxed animate-fade-in-up delay-100">
              Track, confirm, and secure your mobile money transactions with enterprise-grade 
              infrastructure built for Liberia and Africa.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up delay-200">
              <Link to="/contact?type=demo">
                <Button className="btn-primary text-base px-8 py-6" data-testid="hero-request-demo-btn">
                  Request Demo
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/about">
                <Button variant="outline" className="btn-secondary text-base px-8 py-6" data-testid="hero-learn-more-btn">
                  Learn More
                </Button>
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="mt-16 flex flex-wrap justify-center gap-6 animate-fade-in delay-300">
              {trustIndicators.map((item, index) => (
                <div key={index} className="flex items-center gap-2 text-slate-500">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  <span className="text-sm font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="section-padding bg-white" data-testid="problem-section">
        <div className="container-main">
          <div className="text-center mb-16">
            <span className="text-sm font-medium text-[#0080FF] uppercase tracking-widest mb-4 block">
              The Challenge
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              Managing Mobile Money is Hard
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Businesses in Liberia face unique challenges when it comes to mobile money management.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {problems.map((problem, index) => (
              <div 
                key={index}
                className="feature-card text-center"
                data-testid={`problem-card-${index}`}
              >
                <div className="icon-container mx-auto mb-6">
                  {problem.icon}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">
                  {problem.title}
                </h3>
                <p className="text-slate-600">
                  {problem.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="section-padding bg-slate-50" data-testid="solutions-section">
        <div className="container-main">
          <div className="text-center mb-16">
            <span className="text-sm font-medium text-[#0080FF] uppercase tracking-widest mb-4 block">
              Our Solutions
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              Built for African Businesses
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Two powerful products designed to solve your mobile money challenges.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {solutions.map((solution, index) => (
              <div 
                key={index}
                className="bg-white rounded-3xl p-8 lg:p-10 border border-slate-100 card-hover"
                data-testid={`solution-card-${solution.title.toLowerCase()}`}
              >
                <div className="w-16 h-16 mb-6">
                  <img 
                    src={solution.icon} 
                    alt={solution.title} 
                    className="w-full h-full object-contain"
                  />
                </div>
                
                <div className="badge badge-blue mb-4">{solution.subtitle}</div>
                
                <h3 className="text-2xl font-semibold text-[#003C83] mb-3">
                  {solution.title}
                </h3>
                
                <p className="text-slate-600 mb-6 leading-relaxed">
                  {solution.description}
                </p>
                
                <ul className="grid grid-cols-2 gap-3 mb-8">
                  {solution.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                
                <Link to={solution.link}>
                  <Button variant="outline" className="btn-secondary" data-testid={`learn-more-${solution.title.toLowerCase()}-btn`}>
                    Learn More
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-[#003C83]" data-testid="stats-section">
        <div className="container-main">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-${index}`}>
                <div className="text-4xl lg:text-5xl font-bold text-white mb-2">
                  {stat.value}
                </div>
                <div className="text-slate-400 font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Serve Section */}
      <section className="section-padding bg-white" data-testid="audience-section">
        <div className="container-main">
          <div className="text-center mb-16">
            <span className="text-sm font-medium text-[#0080FF] uppercase tracking-widest mb-4 block">
              Who We Serve
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              Built for Your Business
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="feature-card" data-testid="audience-local">
              <div className="icon-container mb-6">
                <Building2 className="w-6 h-6 text-[#0080FF]" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                Local Businesses
              </h3>
              <p className="text-slate-600">
                Merchants, shops, and service providers who accept mobile money payments daily.
              </p>
            </div>
            
            <div className="feature-card" data-testid="audience-diaspora">
              <div className="icon-container mb-6">
                <Globe className="w-6 h-6 text-[#0080FF]" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                Diaspora Entrepreneurs
              </h3>
              <p className="text-slate-600">
                Business owners abroad who need to monitor their Liberian operations remotely.
              </p>
            </div>
            
            <div className="feature-card" data-testid="audience-enterprise">
              <div className="icon-container mb-6">
                <Users className="w-6 h-6 text-[#0080FF]" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                Enterprises
              </h3>
              <p className="text-slate-600">
                Large organizations needing robust transaction infrastructure and compliance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-br from-[#003C83] to-[#00479E]" data-testid="cta-section">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
              Ready to Secure Your Transactions?
            </h2>
            <p className="text-slate-300 text-lg mb-10 leading-relaxed">
              Join hundreds of businesses already using TrustBridge to manage their mobile money operations with confidence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact?type=demo">
                <Button className="btn-accent text-base px-8 py-6" data-testid="cta-request-demo-btn">
                  Request Demo
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="bg-transparent text-white border-white/30 hover:bg-white/10 rounded-full px-8 py-6 text-base" data-testid="cta-contact-btn">
                  Contact Sales
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
